import React, {useEffect, useRef, useState} from "react";
import {removeItem, insertItem, toggleCollapseAtPath, getDepthFromPath} from "./NestableUtils"
import { NestableItem } from "./NestableItem";
import "./Nestable.scoped.scss"
import { StepTypes } from "interfaces/sw/SWInterfaces";
 
type DragState = {
  path: number[];
  position: "before" | "after" | "inside";
  height?: number;
}
 
type Props = {
  items: Item[];
  renderItem: (params: {
    item: Item;
    depth: number;
    collapseIcon: React.ReactNode;
  }) => React.ReactNode;
  onChange?: (params: {
    items: Item[];
    dragItem: Item;
    targetPath: number[];
  }) => boolean;
  childrenProps?: string;
  maxDepth?: number;
  renderCollapseIcon?: any;
  displayAll?: boolean;
  isStepList?: boolean;
}
 
export type Item = {
    id: string;
    text: string;
    children?: Item[];
    nestedIn?: string | null;
    link?: string | null;
    type?: number;
    parentIndex?: string;
    parentGuid?: string | null;
    parentId?: number | null;
    sortOrder?: number;
    stepId?: number;
    isConditional?: boolean;
    hasUnResolvedComments?: boolean;
    isTask?: boolean;
    stepType?: StepTypes,
    isInTask?: boolean;
    isRCTask?: boolean;
    rcContainer?: boolean;
    hasMasterStep?: boolean;
    isRCStep?: boolean;
    stepNumber?: string;
    checked?: boolean;
    depth?: number;
    isEnabled?: boolean;
    collapsed?: boolean;
    [key: string]: any;
  };

  const DRAG_START_THRESHOLD = 4;
  const AUTO_SCROLL_EDGE = 48;
  const AUTO_SCROLL_MAX_SPEED = 18;

  // walks up from the nestable root to find the nearest ancestor that actually scrolls
  const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
    let node = el?.parentElement ?? null;
    while (node) {
      const style = window.getComputedStyle(node);
      if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  const resolveHitTarget = (clientX: number, clientY: number): { path: number[]; position: DragState["position"] } | null => {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    const target = el?.closest("[data-nestable-path]") as HTMLElement | null;
    if (!target) return null;

    const pathAttr = target.getAttribute("data-nestable-path");
    const positionAttr = target.getAttribute("data-nestable-position") as DragState["position"] | null;
    if (!pathAttr || !positionAttr) return null;

    return {
      path: pathAttr.split(",").map(Number),
      position: positionAttr
    }
  }
 
 
  export const Nestable: React.FC<Props> = ({
    items: initialItems,
    renderItem,
    onChange,
    renderCollapseIcon,
    childrenProps = "children",
    maxDepth = 3,
    displayAll = false,
    isStepList = true
  }) => {
    const [items, setItems] = useState<Item[]>(initialItems);
    const [dragState, setDragState] = useState<DragState | null>(null);
    const dragRef = useRef<{item: Item; path: number[], height: number} | null>(null);
    const pendingDragRef = useRef<{item: Item; path: number[]; height: number; startX: number; startY: number} | null>(null);
    const dragStateRef = useRef<DragState | null>(null);
    const itemsRef = useRef<Item[]>(items);
    const onChangeRef = useRef(onChange);
    const scrollParentRef = useRef<HTMLElement | null>(null);
    const lastPointerPosRef = useRef<{x: number; y: number}>({x: 0, y: 0});
    const autoScrollFrameRef = useRef<number | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const prevInitialItemsRef = useRef<Item[]>(initialItems);

    dragStateRef.current = dragState;
    itemsRef.current = items;
    onChangeRef.current = onChange;

 
    const normalizePathAfterRemoval = (
      path: number[],
      removedPath: number[]
    ): number[] => {
      const normalized = [...path];
      const max = Math.min(normalized.length, removedPath.length);
 
      let divergeAt = 0;
      while (divergeAt < max && normalized[divergeAt] === removedPath[divergeAt]) {
        divergeAt += 1;
      }
 
      // Only shift the index when the removed item came from the same siblings list.
      // If removal happened deeper in a subtree, parent-level indexes do not change.
      if (
        divergeAt < max &&
        removedPath.length - 1 === divergeAt &&
        normalized[divergeAt] > removedPath[divergeAt]
      ) {
        normalized[divergeAt] -= 1;
      }
 
      return normalized;
    }
 
    const getFinalPath = (
      dragPath: number[],
      targetPath: number[],
      position: DragState["position"]
    ) : number[] => {
      const normalizedTargetPath = normalizePathAfterRemoval(targetPath, dragPath);
 
      if(position === "inside"){
        return [...normalizedTargetPath, 0]
      }
 
      const targetParent = normalizedTargetPath.slice(0, -1)
      const targetIndex = normalizedTargetPath[normalizedTargetPath.length - 1];
 
        return [
          ...targetParent,
          position === "after" ? targetIndex + 1 : targetIndex
        ]
    }
 
    const isSamePosition = (
      dragPath: number[],
      targetPath: number[],
      position: DragState["position"]
    ) => {
      const finalPath = getFinalPath(dragPath, targetPath, position)
      return finalPath.join(",") === dragPath.join(",")
    }
 
    const canDrop = (
      dragPath: number[],
      targetPath: number[],
      position: DragState["position"]
    ) => {
      return isDropAllowed(dragPath, targetPath, position);
    }
 
    useEffect(() => {
      const previousSnapshot = JSON.stringify(prevInitialItemsRef.current);
      const currentSnapshot = JSON.stringify(initialItems);

      if (previousSnapshot !== currentSnapshot) {
        setItems(initialItems);
      }

      prevInitialItemsRef.current = initialItems;
    }, [initialItems])
 
    const isDropAllowed = (
      dragPath: number[],
      targetPath: number[],
      position: DragState["position"]
    ) => {
      const targetDepth = getDepthFromPath(targetPath);
 
      if(targetPath.join(",").startsWith(dragPath.join(","))) return false;
 
      const finalDepth = position === "inside" ? targetDepth + 1 : targetDepth;
 
      if(finalDepth >= maxDepth) return false;
 
      if(isSamePosition(dragPath, targetPath, position)) return false;
 
      return true;
    }
 
    const handleToggle = (path: number[]) => {
      setItems(prevItems => { return toggleCollapseAtPath(prevItems, path, childrenProps)});
   
    }
 
    const handleDrop = (dragPath: number[], targetPath: number[], position: DragState["position"]) => {
      if(!dragRef.current) return;
 
      if(!isDropAllowed(dragPath, targetPath, position)){
        setDragState(null);
        return;
      }
 
      const {list: afterRemove, removed} = removeItem(itemsRef.current, dragPath, childrenProps);
 
      let finalPath: number[] = getFinalPath(dragPath, targetPath, position);
 
      const newItems = insertItem(
        afterRemove,
        finalPath,
        removed,
        childrenProps
      )
      setDragState(null);
      dragRef.current = null;
 
      const payload = {
        items: newItems,
        dragItem: removed,
        targetPath: finalPath
      }
 
      if(onChangeRef.current){
        let result: boolean = onChangeRef.current(payload);
        if(result){
          setItems(newItems)
        }
       
      }
    }
 
    const updateDragState = (state: DragState | null) => {
      if(!dragRef.current) return;

      if(state && !canDrop(dragRef.current.path, state.path, state.position)) return;

      setDragState(state);
    }

    const stopAutoScroll = () => {
      if(autoScrollFrameRef.current !== null){
        cancelAnimationFrame(autoScrollFrameRef.current);
        autoScrollFrameRef.current = null;
      }
    }

    const runAutoScroll = () => {
      const scrollParent = scrollParentRef.current;

      if(scrollParent){
        const rect = scrollParent.getBoundingClientRect();
        const { y } = lastPointerPosRef.current;
        const distanceFromTop = y - rect.top;
        const distanceFromBottom = rect.bottom - y;

        if(distanceFromTop >= 0 && distanceFromTop < AUTO_SCROLL_EDGE){
          scrollParent.scrollTop -= AUTO_SCROLL_MAX_SPEED * (1 - distanceFromTop / AUTO_SCROLL_EDGE);
        } else if(distanceFromBottom >= 0 && distanceFromBottom < AUTO_SCROLL_EDGE){
          scrollParent.scrollTop += AUTO_SCROLL_MAX_SPEED * (1 - distanceFromBottom / AUTO_SCROLL_EDGE);
        }
      }

      autoScrollFrameRef.current = requestAnimationFrame(runAutoScroll);
    }

    const beginDrag = () => {
      const pending = pendingDragRef.current;
      if(!pending) return;

      dragRef.current = { item: pending.item, path: pending.path, height: pending.height };
      setDragState({ path: pending.path, position: "inside", height: pending.height });

      scrollParentRef.current = findScrollParent(rootRef.current);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";

      stopAutoScroll();
      autoScrollFrameRef.current = requestAnimationFrame(runAutoScroll);
    }

    const endDrag = () => {
      if(dragRef.current && dragStateRef.current){
        handleDrop(dragRef.current.path, dragStateRef.current.path, dragStateRef.current.position);
      }

      dragRef.current = null;
      pendingDragRef.current = null;
      scrollParentRef.current = null;
      stopAutoScroll();
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }

    useEffect(() => {
      const handlePointerMove = (e: PointerEvent) => {
        lastPointerPosRef.current = { x: e.clientX, y: e.clientY };

        if(!dragRef.current && pendingDragRef.current){
          const { startX, startY } = pendingDragRef.current;
          if(Math.hypot(e.clientX - startX, e.clientY - startY) >= DRAG_START_THRESHOLD){
            beginDrag();
          }
          return;
        }

        if(dragRef.current){
          const hit = resolveHitTarget(e.clientX, e.clientY);
          if(hit){
            updateDragState({ ...hit, height: dragRef.current.height });
          }
        }
      }

      const handlePointerUp = () => {
        if(dragRef.current || pendingDragRef.current){
          endDrag();
        }
      }

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);

      return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
        stopAutoScroll();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleItemPointerDown = (data: { item: Item; path: number[]; height: number; clientX: number; clientY: number }) => {
      pendingDragRef.current = {
        item: data.item,
        path: data.path,
        height: data.height,
        startX: data.clientX,
        startY: data.clientY
      };
    }
 
    return (
      <div className="nestable" ref={rootRef}>
        <ul className="nestable-list">
          {items.map((item, i) => (
            <NestableItem
              key={item.id}
              item={item}
              depth={0}
              path={[i]}
              renderItem={renderItem}
              childrenProps={childrenProps}
              dragState={dragState}
              displayAll={displayAll}
              onToggle={handleToggle}
              onItemPointerDown={handleItemPointerDown}
              isStepList={isStepList}
              renderCollapseIcon={renderCollapseIcon}
              />
          ))}
        </ul>
      </div>
    )
  }